import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { roleLabels } from '@/types/database';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Bell,
  Menu,
  ChevronDown,
  Settings,
  LogOut,
  HelpCircle,
  AlertCircle,
  Users,
  ShieldCheck,
  CreditCard,
  Shield,
  BarChart3,
  X,
  MapPin,
  Layers,
  Activity,
  FileText,
  Smartphone,
} from 'lucide-react';
import logoUrgSalud from '@/assets/logo-urgsalud-transparent.png';
import { NotificacionesPopover } from './NotificacionesPopover';

const adminNavItems = [
  { label: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { label: 'Monitoreo en Vivo', href: '/admin/monitoreo', icon: Activity },
  { label: 'Notificaciones', href: '/admin/notificaciones', icon: Bell },
  { label: 'Usuarios', href: '/admin/usuarios', icon: Users },
  { label: 'Verificación Profesionales', href: '/admin/verificacion', icon: ShieldCheck },
  { label: 'Moderación Solicitudes', href: '/admin/solicitudes', icon: AlertCircle },
  { label: 'Pagos y Suscripciones', href: '/admin/pagos', icon: CreditCard },
  { label: 'Comunas y Regiones', href: '/admin/comunas', icon: MapPin },
  { label: 'Áreas de Servicio', href: '/admin/areas', icon: Layers },
  { label: 'Contenido', href: '/admin/contenido', icon: FileText },
  { label: 'Gestión PWA', href: '/admin/pwa', icon: Smartphone },
  { label: 'Reportes', href: '/admin/reportes', icon: BarChart3 },
  { label: 'Configuración', href: '/admin/configuracion', icon: Settings },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { profile, signOut } = useAuth();
  const location = useLocation();

  const currentPageTitle = adminNavItems.find(item => item.href === location.pathname)?.label || 'Administración';

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Admin Theme (Dark/Neutral) */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-full w-64 bg-gradient-to-b from-slate-900 to-slate-800 transform transition-transform duration-300 lg:translate-x-0 shadow-xl',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo & Close button (mobile) */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <img src={logoUrgSalud} alt="UrgSalud Chile" className="h-9 w-auto brightness-0 invert" width={36} height={36} />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white/70 hover:text-white hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Role indicator */}
          <div className="px-5 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-role-admin/20">
                <Shield className="w-4 h-4 text-role-admin" />
              </div>
              <div>
                <span className="text-xs text-white/60 uppercase tracking-wide font-medium">Panel de</span>
                <p className="text-sm font-semibold text-white">Administración</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {adminNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <li key={item.href}>
                    <NavLink
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                        isActive
                          ? 'bg-role-admin text-white shadow-md'
                          : 'text-white/70 hover:text-white hover:bg-white/10'
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* System status */}
          <div className="px-4 py-3 mx-3 mb-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center justify-between text-xs text-white/70">
              <span>Sistema</span>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-success" />
                <span className="text-success">Operativo</span>
              </div>
            </div>
          </div>

          {/* User section at bottom */}
          <div className="border-t border-white/10 p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 ring-2 ring-role-admin/30">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-role-admin/20 text-role-admin text-sm font-medium">
                  {profile?.nombre?.[0]}{profile?.apellido?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {profile?.nombre} {profile?.apellido}
                </p>
                <p className="text-xs text-role-admin truncate">
                  Administrador
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="w-5 h-5" />
            </Button>

            {/* Page title */}
            <div className="hidden lg:flex items-center gap-3">
              <h1 className="text-lg font-semibold text-foreground">{currentPageTitle}</h1>
              <Badge className="bg-role-admin/15 text-role-admin text-xs">
                <Shield className="w-3 h-3 mr-1" />
                Admin
              </Badge>
            </div>

            {/* Mobile logo */}
            <div className="lg:hidden">
              <img src={logoUrgSalud} alt="UrgSalud" className="h-8 w-auto" width={32} height={32} />
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <NotificacionesPopover />

              {/* User dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar_url || undefined} />
                      <AvatarFallback className="bg-role-admin/10 text-role-admin text-xs">
                        {profile?.nombre?.[0]}{profile?.apellido?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium">{profile?.nombre}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col gap-1">
                      <span>{profile?.nombre} {profile?.apellido}</span>
                      <Badge className="w-fit text-xs bg-role-admin/15 text-role-admin">
                        {roleLabels.admin}
                      </Badge>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <NavLink to="/admin/configuracion" className="flex items-center gap-2 cursor-pointer">
                      <Settings className="w-4 h-4" />
                      Configuración
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <NavLink to="/ayuda" className="flex items-center gap-2 cursor-pointer">
                      <HelpCircle className="w-4 h-4" />
                      Ayuda
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={signOut}
                    className="text-destructive focus:text-destructive cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
